import { ArrowDownTrayIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, ButtonGroup, Modal, PlainTable, TextArea } from 'stories/components'
import { type CategoryDocument, CategoryDocuments } from 'stories/components/CategoryFileTable/CategoryFileTable'
import { formatTime } from 'utils'

import { AppraisalDocumentCategories, AppraisalOrderFile } from '../types'
import { handleFailedDocsNotification } from './handleFailedDocs'

/** Last successful Notification Logs per vendor order — survive close/reopen races. */
const vendorLogsCache = new Map<string, any[][]>()

const vendorLogsCacheKey = (data: Record<string, any>) => {
  const api = data?.thirdPartyAPI || {}
  const id = api.appraisalID || api.OrderId || api.orderID || data?.id || ''
  return `${api.type || 'vendor'}:${id}`
}

export interface AppraisalVendorModalProps {
  title: string
  data: Record<string, any>
  onClose: Function
  docTypes?: string[]
  fetchLogs: () => Promise<{ success: boolean; [key: string]: any }>
  mapLogs?: (res: any) => any[][]
  reverseOrder?: boolean
  postComment: (comment: string) => Promise<any>
  postDocuments: (id: number, documents: CategoryDocument[]) => Promise<{ uploadedDocs: string[]; failedDocs?: any[] }>
  menuTabs?: string[]
  sendMessageLabel?: string
  uploadDocumentsLabel?: string
  renderProgressSection?: ReactNode
  renderProgressHeader?: ReactNode
  renderExtraTabContent?: (menu: string) => ReactNode | null
  renderAfterTabs?: ReactNode
  extraLoading?: boolean
  contentClassName?: string
  onLogsLoaded?: (res: any) => void
  onMount?: (setLoading: Dispatch<SetStateAction<boolean>>) => void
  orderFiles?: AppraisalOrderFile[]
  onImportDocuments?: () => Promise<{ success?: boolean; documents?: AppraisalOrderFile[] }>
}

export const AppraisalVendorModal = ({
  title,
  data,
  onClose,
  docTypes,
  fetchLogs,
  mapLogs = (res: any) =>
    res.data.map((item: any, index: number) => [
      index + 1,
      <div key={index}>
        {formatTime(item.comment_date)}
        <br />
        <span className="italic">{item.comment_by}</span>
      </div>,
      item.comment,
    ]),
  reverseOrder = false,
  postComment,
  postDocuments,
  menuTabs = ['Notification Logs', 'Documents'],
  sendMessageLabel = 'Send Message',
  uploadDocumentsLabel = 'Upload Documents',
  renderProgressSection,
  renderProgressHeader,
  renderExtraTabContent,
  renderAfterTabs,
  extraLoading,
  contentClassName = 'mt-4',
  onLogsLoaded,
  onMount,
  orderFiles = [],
  onImportDocuments,
}: AppraisalVendorModalProps) => {
  const cacheKey = vendorLogsCacheKey(data)
  const [menu, setMenu] = useState(menuTabs[0])
  const [logs, setLogs] = useState<any[][]>(() => vendorLogsCache.get(cacheKey) || [])
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [action, setAction] = useState('')
  const [documents, setDocuments] = useState<CategoryDocument[]>(data.info?.documents || [])
  const [uploadedDocs, setUploadedDocs] = useState<string[]>(data.thirdPartyAPI.uploadedDocs ?? [])
  const [importedFiles, setImportedFiles] = useState<AppraisalOrderFile[]>([])
  const [importing, setImporting] = useState(false)

  const applyLogs = (res: any) => {
    // Keep vendor-specific payloads intact (e.g. Trinity uses `comments`, not `data`).
    // Only reverse when the shared `data` array shape is present.
    const payload =
      reverseOrder && Array.isArray(res?.data) ? { ...res, data: [...res.data].reverse() } : res
    const mapped = mapLogs(payload)
    setLogs(mapped)
    // Never cache an empty fetch — a post-create race used to stick a blank table
    // on reopen even after comments existed at the AMC.
    if (mapped.length) vendorLogsCache.set(cacheKey, mapped)
    else vendorLogsCache.delete(cacheKey)
    onLogsLoaded?.(res)
  }

  const isRetryableLogFailure = (res?: any, err?: any) => {
    if (err) return true
    if (!res || res.success) return false
    const msg = `${res.message || ''}`.toLowerCase()
    // Do not retry hard auth/config failures — retries spam toasts for Elite/AN/etc.
    if (
      msg.includes('credential') ||
      msg.includes('unauthorized') ||
      msg.includes('forbidden') ||
      msg.includes('permission') ||
      msg.includes('invalid user')
    ) {
      return false
    }
    // Retry transient ValueLink / network-style empties.
    return (
      msg.includes('notification logs') ||
      msg.includes('try again') ||
      msg.includes('timeout') ||
      msg.includes('connection') ||
      !msg
    )
  }

  useEffect(() => {
    let cancelled = false
    const cached = vendorLogsCache.get(cacheKey)
    if (cached?.length) setLogs(cached)

    const load = async (attempt = 0) => {
      if (!cached?.length) setLoading(true)
      try {
        const res = await fetchLogs()
        if (cancelled) return
        if (res?.success) {
          const rowCount = Array.isArray(res.data)
            ? res.data.length
            : Array.isArray(res.comments)
              ? res.comments.length
              : 0
          // ValueLink often returns an empty list for a beat after create / overlapping SOAP.
          if (!rowCount && attempt < 2) {
            await new Promise((r) => setTimeout(r, 700 * (attempt + 1)))
            if (!cancelled) await load(attempt + 1)
            return
          }
          applyLogs(res)
        } else if (attempt < 2 && isRetryableLogFailure(res)) {
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)))
          if (!cancelled) await load(attempt + 1)
          return
        } else if (!vendorLogsCache.get(cacheKey)?.length) {
          setLogs([])
          // Api layer often already toasted success+message responses; avoid duplicates.
          if (res?.message && res?.success === undefined) toast(res.message, { type: 'error' })
        }
        // If we still have cached rows, keep them and skip the toast — reopen race.
      } catch (e: any) {
        if (cancelled) return
        if (attempt < 2 && isRetryableLogFailure(undefined, e)) {
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)))
          if (!cancelled) await load(attempt + 1)
          return
        }
        if (!vendorLogsCache.get(cacheKey)?.length) {
          setLogs([])
          toast(e?.message || 'Failed to load notification logs.', { type: 'error' })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    onMount?.(setLoading)
    return () => {
      cancelled = true
    }
  }, [cacheKey])

  const getLogs = async () => {
    setLoading(true)
    try {
      const res = await fetchLogs()
      if (res?.success) {
        applyLogs(res)
      } else if (!vendorLogsCache.get(cacheKey)?.length) {
        setLogs([])
        if (res?.message) toast(res.message, { type: 'error' })
      }
    } catch (e: any) {
      if (!vendorLogsCache.get(cacheKey)?.length) {
        setLogs([])
        toast(e?.message || 'Failed to load notification logs.', { type: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }

  const onSendMessage = async () => {
    setLoading(true)
    setAction('sendMessage')
    await postComment(comment)
    setComment('')
    setLoading(false)
    setAction('')
    getLogs()
  }

  const extraDocs = useMemo(
    () => documents.filter((item) => !uploadedDocs?.includes(item.fileKey)),
    [documents, uploadedDocs],
  )

  const sendDocuments = async () => {
    if (docTypes?.length) {
      const nonTypeDocs = extraDocs.filter((v) => !v.type).length
      if (nonTypeDocs) {
        toast('Please select file type of the new files.', { type: 'warning' })
        return
      }
    }
    setLoading(true)
    setAction('sendDocuments')
    const res = await postDocuments(data.id, extraDocs)
    handleFailedDocsNotification(res)
    const tempDocs = cloneDeep(uploadedDocs)
    res.uploadedDocs.forEach((fileKey: string) => tempDocs.push(fileKey))
    setUploadedDocs(tempDocs)
    setLoading(false)
    setAction('')
  }

  const onRefreshImport = async () => {
    if (!onImportDocuments) return
    setImporting(true)
    try {
      const res = await onImportDocuments()
      if (res?.documents) setImportedFiles(res.documents)
      toast('Documents imported from the vendor.', { type: 'success' })
    } catch {
      toast('Failed to import documents from the vendor.', { type: 'error' })
    }
    setImporting(false)
  }

  const renderContent = () => {
    if (menu === 'Notification Logs') {
      return (
        <div>
          <PlainTable header={['No', 'Date / By', 'Comment']} tdClass="px-4 py-2" data={logs} />
          <TextArea title="Comment" value={comment} onChange={(value) => setComment(value)} />
          <div className="text-center mt-3">
            <Button
              color="gray"
              onClick={onSendMessage}
              loading={action === 'sendMessage'}
              disabled={comment.length === 0}
            >
              {sendMessageLabel}
            </Button>
          </div>
        </div>
      )
    }
    if (menu === 'Documents') {
      return (
        <div>
          <CategoryDocuments
            documents={documents}
            onChange={(docs: CategoryDocument[]) => setDocuments(docs)}
            setLoading={setLoading}
            type="OrderAppraisal"
            categories={AppraisalDocumentCategories}
            types={docTypes}
          />
          <div className="text-center mt-4">
            <Button
              color="gray"
              onClick={sendDocuments}
              loading={action === 'sendDocuments'}
              disabled={extraDocs.length === 0}
            >
              {uploadDocumentsLabel}
            </Button>
          </div>
          {orderFiles.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <div className="font-bold text-sm text-gray-600 mb-2">Uploaded Documents</div>
              <PlainTable
                header={['No', 'Type', 'Name', 'Action']}
                tdClass="px-4 py-2"
                classNames={['', '', '', 'w-16 text-center']}
                data={[...orderFiles]
                  .sort((a, b) => a.category.localeCompare(b.category))
                  .map((doc, index) => [
                    index + 1,
                    doc.category,
                    doc.name,
                    <a
                      key={doc.id}
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-shade-blue flex justify-center"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </a>,
                  ])}
              />
            </div>
          )}
          {onImportDocuments && (
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="font-bold text-sm text-gray-600">Imported Documents</div>
                <button
                  type="button"
                  onClick={onRefreshImport}
                  disabled={importing}
                  className="text-shade-blue disabled:opacity-50"
                  title="Import documents from the vendor"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${importing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {importedFiles.length > 0 ? (
                <PlainTable
                  header={['No', 'Type', 'Name', 'Action']}
                  tdClass="px-4 py-2"
                  classNames={['', '', '', 'w-16 text-center']}
                  data={importedFiles.map((doc, index) => [
                    index + 1,
                    doc.category,
                    doc.name,
                    <a
                      key={doc.id}
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-shade-blue flex justify-center"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </a>,
                  ])}
                />
              ) : (
                <div className="text-sm text-gray-400 italic">
                  Press the refresh button to import documents from the vendor.
                </div>
              )}
            </div>
          )}
        </div>
      )
    }
    return renderExtraTabContent?.(menu) ?? null
  }

  const showProgressSection = !!(renderProgressSection || renderProgressHeader)

  return (
    <Modal isOpen title={title} titleOkay="" onClose={() => onClose(false)}>
      <div className="relative w-[calc(100vw-5rem)] max-w-2xl overflow-hidden text-md text-gray-600">
        <LayoutLoading show={loading || !!extraLoading} />
        {showProgressSection && (
          <div>
            <div className="border-b mb-2 flex justify-between flex-wrap gap-4">
              <div className="italic font-bold">Progress Status</div>
              {renderProgressHeader}
            </div>
            {renderProgressSection}
          </div>
        )}
        <div className="overflow-x-auto rounded border-[4px] border-shade-blue/50 p-2">
          <ButtonGroup title={menuTabs} onChange={(key) => setMenu(key)} value={menu} />
        </div>
        {renderAfterTabs}
        <div className={contentClassName}>{renderContent()}</div>
      </div>
    </Modal>
  )
}
